// Pure renderer: turns a ClaimProjection into a plain-text transcript that a
// human (or an LLM) can eyeball alongside the product mechanics. Engineering-
// internal review tool — NOT a customer-facing export. See
// `docs/calculation-projection-prompt.md` for the rationale.
//
// The five sections (header / returns / year snapshots / months / brief) mirror
// the on-page projection-details view so a reviewer can cross-check the
// transcript against the UI and against the product brief.

import { CareEnvironment, CarePhase } from '../../../../appModel';
import {
  getMonthPayout,
  getRemainingDeathBenefit,
  getTotalIndemnitySurplus,
  getTotalIneligibleCost,
  getTotalLtcCoveragePayout,
  getTotalNonLtcPayout,
  getTotalPayout,
  getTotalProjectedCareCost,
} from '../projectionTotals';
import {
  currency,
  currencyCents,
  delta,
  fixedWidthTable,
  labelledAmounts,
  pct,
  TranscriptColumn,
} from './formatters';

export interface TranscriptOptions {
  // Human-friendly strategy label for the title (e.g. "CareMatters II"). Falls
  // back to `terms.strategyId`.
  strategyDisplayName?: string;
  // The hand-written product brief for this strategy, appended verbatim as the
  // final section. Resolve it from `briefs/` keyed off `terms.strategyId`.
  brief?: string;
}

type Claimant = NonNullable<MonthContext['claimant']>;

export function renderTranscript(
  projection: ClaimProjection,
  options: TranscriptOptions = {},
): string {
  const { terms, yearSnapshots, months } = projection;
  const isJoint = months.some(m => m.context.claimant != null);

  const sections = [
    renderTitle(terms, options),
    renderHeaderBlock(terms),
    renderReturnsSummary(months),
    renderYearSnapshots(yearSnapshots),
    ...renderMonthSections(months, isJoint),
  ];

  if (options.brief?.trim()) {
    sections.push(`## Product brief\n${options.brief.trim()}`);
  }

  return sections.join('\n\n') + '\n';
}

// Title deliberately omits any client identifier — the transcript is meant to
// be pasted into external review tools, so it must stay free of PII/PHI. Only
// the (non-identifying) product strategy is named.
function renderTitle(terms: ClaimTerms, options: TranscriptOptions): string {
  const strategy = options.strategyDisplayName ?? terms.strategyId;
  return `# Projection — ${strategy}`;
}

function renderHeaderBlock(terms: ClaimTerms): string {
  const lines: string[] = [];

  const riderDaily = terms.nominalDailyBenefit - terms.nominalDailyBenefitBase;
  lines.push(
    `at_issue_daily_benefit: ${currencyCents(terms.nominalDailyBenefit)} ` +
      `(base ${currencyCents(terms.nominalDailyBenefitBase)} / rider ${currencyCents(riderDaily)})`,
  );

  const inflationMode = terms.inflationIsSimple ? 'simple' : 'compound';
  lines.push(
    `inflation_rate: ${pct(terms.inflationRate)} ${inflationMode}, ${capLabel(terms.inflationCapYears)}`,
  );

  if (terms.baseBenefitPoolInflationRate != null) {
    lines.push(
      `base_pool_inflation: ${pct(terms.baseBenefitPoolInflationRate)}` +
        capSuffix(terms.baseBenefitPoolInflationCapYears),
    );
  }
  if (terms.extensionOfBenefitsPoolInflationRate != null) {
    lines.push(
      `eob_pool_inflation: ${pct(terms.extensionOfBenefitsPoolInflationRate)}` +
        capSuffix(terms.extensionOfBenefitsPoolInflationCapYears),
    );
  }
  if (terms.baseBenefitPoolAtIssue != null) {
    lines.push(
      `base_pool_at_issue: ${
        terms.isUnlimited ? 'Unlimited' : currency(terms.baseBenefitPoolAtIssue)
      }`,
    );
  }
  if (terms.extensionOfBenefitsPoolAtIssue != null) {
    lines.push(`eob_pool_at_issue: ${currency(terms.extensionOfBenefitsPoolAtIssue)}`);
  }

  lines.push(`care_cost_inflation: ${pct(terms.careCostInflationRate)} / yr`);
  lines.push(
    `projection_anchor_year: ${terms.projectionAnchorYear}, ` +
      `first_phase_start_year: ${terms.firstPhaseStartYear}, ` +
      `policy_premium_start_year: ${terms.policyPremiumStartYear}`,
  );
  lines.push(`minimum_guaranteed_death_benefit: ${currency(terms.minimumGuaranteedDeathBenefit)}`);
  lines.push(`payout_model: ${payoutModel(terms)}`);
  lines.push(`strategy_id: ${terms.strategyId}`);

  return lines.join('\n');
}

function capLabel(capYears: number): string {
  return capYears > 0 ? `capped at ${capYears} yr` : 'uncapped';
}

function capSuffix(capYears: number | undefined): string {
  return capYears != null && capYears > 0 ? `, capped at ${capYears} yr` : ', uncapped';
}

function payoutModel(terms: ClaimTerms): string {
  if (terms.isIndemnity) return 'indemnity';
  if (terms.isReimbursement) return 'reimbursement';
  return 'unknown';
}

function renderReturnsSummary(months: MonthlyEntry[]): string {
  const rows = [
    { label: 'LTC payout', value: currency(getTotalLtcCoveragePayout(months)) },
    { label: 'Indemnity surplus', value: currency(getTotalIndemnitySurplus(months)) },
    { label: 'Remaining death benefit', value: currency(getRemainingDeathBenefit(months)) },
    { label: 'Non-LTC payout', value: currency(getTotalNonLtcPayout(months)) },
    { label: 'Total payout', value: currency(getTotalPayout(months)) },
    { label: 'Total projected LTC cost', value: currency(getTotalProjectedCareCost(months)) },
    { label: 'Ineligible cost (OOP)', value: currency(getTotalIneligibleCost(months)) },
  ];
  return `## Returns summary\n${labelledAmounts(rows)}`;
}

function renderYearSnapshots(snapshots: YearSnapshot[]): string {
  const columns: TranscriptColumn[] = [
    { header: 'yr', align: 'right' },
    { header: 'cal+', align: 'right' },
    { header: 'daily_base', align: 'right' },
    { header: 'daily_rider', align: 'right' },
    { header: 'daily_eff', align: 'right' },
    { header: 'monthly_cap', align: 'right' },
    { header: 'pool_ceiling', align: 'right' },
  ];
  const rows = snapshots.map(s => [
    String(s.policyYear),
    String(s.calendarYearOffset),
    currencyCents(s.effectiveDailyBenefitBase),
    currencyCents(s.effectiveDailyBenefitRider),
    currencyCents(s.effectiveDailyBenefit),
    currency(s.monthlyBenefitCap),
    currency(s.theoreticalLtcPoolCeiling),
  ]);
  return `## Year snapshots\n${fixedWidthTable(columns, rows)}`;
}

function renderMonthSections(months: MonthlyEntry[], isJoint: boolean): string[] {
  if (!isJoint) {
    return [`## Months\n${renderMonthTable(months, false)}`];
  }

  // Joint tables are titled by the (non-identifying) claimant role tag, never a
  // client name — see `renderTitle` on the PII/PHI constraint.
  const claimants: Claimant[] = ['client', 'partner'];
  return claimants
    .map(claimant => {
      const claimantMonths = months.filter(m => m.context.claimant === claimant);
      if (claimantMonths.length === 0) return null;
      return `## Months — ${claimant}\n${renderMonthTable(claimantMonths, false)}`;
    })
    .filter((s): s is string => s != null);
}

function renderMonthTable(months: MonthlyEntry[], includeClaimant: boolean): string {
  const columns: TranscriptColumn[] = [
    { header: 'idx', align: 'right' },
    { header: 'yr', align: 'right' },
    { header: 'age', align: 'right' },
    ...(includeClaimant ? [{ header: 'claimant', align: 'left' } as TranscriptColumn] : []),
    { header: 'ph/env', align: 'left' },
    { header: 'care_cost', align: 'right' },
    { header: 'payout', align: 'right' },
    { header: 'ineligible', align: 'right' },
    { header: 'base_pool', align: 'right' },
    { header: 'eob', align: 'right' },
    { header: 'db', align: 'right' },
    { header: 'out_of_pocket', align: 'left' },
    { header: 'milestones', align: 'left' },
  ];

  const rows = months.map(month => {
    const { context, facts, openState, state, deltas, events } = month;
    const basePool = state.pools.baseBenefitPool;
    const baseDelta = basePool - openState.pools.baseBenefitPool;
    const eobPool = state.pools.extensionOfBenefitsPool;
    const eobDelta = eobPool - openState.pools.extensionOfBenefitsPool;
    const db = state.pools.deathBenefit;
    const dbDelta = db - openState.pools.deathBenefit;

    return [
      String(context.monthIndex),
      String(context.policyYear),
      context.clientAge != null ? String(context.clientAge) : '—',
      ...(includeClaimant ? [context.claimant ?? '—'] : []),
      `${phaseLabel(context.carePhase)}/${envLabel(context.careEnvironment)}`,
      currency(facts.effectiveCareCost),
      currency(getMonthPayout(month)),
      currency(deltas.payout.ineligibleCost),
      `${currency(basePool)} ${delta(baseDelta)}`,
      `${currency(eobPool)} ${delta(eobDelta)}`,
      `${currency(db)} ${delta(dbDelta)}`,
      renderOutOfPocket(events),
      renderMilestones(events),
    ];
  });

  return fixedWidthTable(columns, rows);
}

// Numeric enums reverse-map value → key name (e.g. 2802 → 'moderateCare'),
// giving readable phase/environment labels without depending on client-side
// label maps. Falls back to the raw value if the reverse lookup misses.
function phaseLabel(phase: CarePhase): string {
  return CarePhase[phase] ?? String(phase);
}

function envLabel(env: CareEnvironment): string {
  return CareEnvironment[env] ?? String(env);
}

// Milestones carry a human-readable `label` ("LTC begins at age 65", "WP
// Satisfied", "LTC pool exhausted", phase/env transitions). Emit both the stable
// `kind` token (which the product briefs reference, e.g. \`ep-lump\`) and the
// label so a reviewer can tie a row to the brief and read what happened.
function renderMilestones(events: MonthlyEvents): string {
  return events.milestones.map(m => `${m.kind}: ${m.label}`).join('; ');
}

// Out-of-pocket day buckets for the row. A single month can STACK reasons — a
// transition month may begin under the 2-ADL gate and end serving the waiting
// period — so render every non-zero bucket, not just the primary reason. Order
// matches the calc's gate-resolution priority so the stack reads chronologically.
// Reason tokens match the briefs' vocabulary (lt-2-adl / in-wp / etc.).
const OUT_OF_POCKET_BUCKETS: Array<{
  reason: ProjectionIneligibilityReason;
  key: keyof IneligibleDayBreakdown;
}> = [
  { reason: 'lt-2-adl', key: 'lt2AdlDays' },
  { reason: 'in-wp', key: 'inWpDays' },
  { reason: 'il-not-covered', key: 'ilNotCoveredDays' },
  { reason: 'pre-policy', key: 'prePolicyDays' },
];

function renderOutOfPocket(events: MonthlyEvents): string {
  const { ineligibleDayBreakdown: breakdown } = events;
  return OUT_OF_POCKET_BUCKETS.filter(b => breakdown[b.key] > 0)
    .map(b => `${b.reason} ${breakdown[b.key]}d`)
    .join(' + ');
}
